import {
  CancellationClaimAmount,
  FamilySituation,
  GeographicalArea,
  TripDuration,
  TripType,
  Variant,
} from '@qover-private/protos-web/qover/dojo/travel/v1/terms';
import { PaymentRythm } from '@qover/domain';
import { Draft } from './Draft';
import { Terms } from './Terms';

export type Premium = {
  withoutTaxes: number;
  withTaxes: number;
  totalTaxes: number;
};

export import Foo = TripType;

class Coverage {
  monthlyPremium: Premium;
  yearlyPremium: Premium;
  constructor(data?: any) {
    this.monthlyPremium = data?.monthlyPremium;
    this.yearlyPremium = data?.yearlyPremium;
  }
}

export type PriceResponse = {
  dimensions: Array<{ name: string; value: string }>;
  coverages: Array<{
    coverageName: string;
    yearlyPremium: Premium;
    monthlyPremium: Premium;
  }>;
};

export interface PriceType {
  dimensions: { [key: string]: any };
  coverages: { [key: string]: any };
}

export class Coverages {
  [CancellationClaimAmount.CANCELLATION_CLAIM_AMOUNT_1K]: Coverage;
  [CancellationClaimAmount.CANCELLATION_CLAIM_AMOUNT_2_5K]: Coverage;
  [CancellationClaimAmount.CANCELLATION_CLAIM_AMOUNT_5K]: Coverage;
  [CancellationClaimAmount.CANCELLATION_CLAIM_AMOUNT_UNSPECIFIED]: Coverage;
  ACCIDENT: Coverage;
  TRAVEL: Coverage;
  BAGGAGE: Coverage;
  constructor(data?: any) {
    this.CANCELLATION_CLAIM_AMOUNT_1K = new Coverage(
      data?.[CancellationClaimAmount.CANCELLATION_CLAIM_AMOUNT_1K],
    );
    this.CANCELLATION_CLAIM_AMOUNT_2_5K = new Coverage(
      data?.[CancellationClaimAmount.CANCELLATION_CLAIM_AMOUNT_2_5K],
    );
    this.CANCELLATION_CLAIM_AMOUNT_5K = new Coverage(
      data?.[CancellationClaimAmount.CANCELLATION_CLAIM_AMOUNT_5K],
    );
    this.ACCIDENT = new Coverage(data?.ACCIDENT);
    this.TRAVEL = new Coverage(data?.TRAVEL);
    this.BAGGAGE = new Coverage(data?.BAGGAGE);
  }

  getCoverage(
    coverage: CancellationClaimAmount | 'ACCIDENT' | 'TRAVEL' | 'BAGGAGE',
  ) {
    return this[coverage];
  }

  getTravel(paymentRythm: PaymentRythm = PaymentRythm.MONTHLY) {
    const cancellationAmount =
      paymentRythm === PaymentRythm.MONTHLY
        ? this.TRAVEL.monthlyPremium.withTaxes
        : this.TRAVEL.yearlyPremium.withTaxes;
    return cancellationAmount;
  }

  getAccident(paymentRythm: PaymentRythm = PaymentRythm.MONTHLY) {
    const cancellationAmount =
      paymentRythm === PaymentRythm.MONTHLY
        ? this.ACCIDENT.monthlyPremium.withTaxes
        : this.ACCIDENT.yearlyPremium.withTaxes;
    return cancellationAmount;
  }

  getBaggage(paymentRythm: PaymentRythm = PaymentRythm.MONTHLY) {
    const cancellationAmount =
      paymentRythm === PaymentRythm.MONTHLY
        ? this.BAGGAGE.monthlyPremium.withTaxes
        : this.BAGGAGE.yearlyPremium.withTaxes;
    return cancellationAmount;
  }

  getCancellation(
    claimAmount: CancellationClaimAmount = CancellationClaimAmount.CANCELLATION_CLAIM_AMOUNT_1K,
    paymentRythm: PaymentRythm = PaymentRythm.MONTHLY,
  ) {
    const cancellationAmount =
      paymentRythm === PaymentRythm.MONTHLY
        ? this[claimAmount].monthlyPremium.withTaxes
        : this[claimAmount].yearlyPremium.withTaxes;
    return cancellationAmount;
  }

  getPrices(
    paymentRythm: PaymentRythm = PaymentRythm.MONTHLY,
    claimAmount: CancellationClaimAmount = CancellationClaimAmount.CANCELLATION_CLAIM_AMOUNT_1K,
  ) {
    return {
      baggage: this.getBaggage(paymentRythm),
      accident: this.getAccident(paymentRythm),
      cancellation: this.getCancellation(claimAmount, paymentRythm),
    };
  }

  computeTotalCoverage(
    terms: Terms,
    paymentRythm: PaymentRythm = PaymentRythm.MONTHLY,
  ) {
    let total = this.getTravel(paymentRythm);

    if (terms.hasAccidentProtection) {
      total += this.getAccident(paymentRythm);
    }

    if (terms.hasBaggageProtection) {
      total += this.getBaggage(paymentRythm);
    }

    if (terms.hasCancellationTrip) {
      total += this.getCancellation(
        terms.cancellationClaimAmount,
        paymentRythm,
      );
    }

    return total;
  }
}

class Price implements PriceType {
  dimensions: { [key: string]: any };
  coverages: { [key: string]: any };
  constructor(obj: PriceResponse) {
    this.dimensions = obj.dimensions.reduce((acc, next) => {
      acc[next.name] = next.value;
      return acc;
    }, {} as { [key: string]: string });
    this.coverages = obj.coverages.reduce(
      (acc, { coverageName, ...coverage }) => {
        acc[coverageName] = coverage;
        return acc;
      },
      {} as { [key: string]: Coverage },
    );
  }
}

export type Prices = {
  [TripType.TRIP_TYPE_SINGLE_TRIP]: any;
  [TripType.TRIP_TYPE_MULTI_TRIP]: any;
  [TripType.TRIP_TYPE_YEAR_TRIP]: any;
};

class PriceInfo {
  priceInfo: Prices;
  basePriceInfo: Prices;

  constructor(pi?: Array<PriceResponse>, basePriceInfo?: Array<PriceResponse>) {
    // @ts-ignore
    this.priceInfo = this.getPrices(pi);
    // @ts-ignore
    this.basePriceInfo = this.getPrices(basePriceInfo);
  }

  private getPrices(pi?: Array<PriceResponse>) {
    const data = pi?.reduce((acc, next) => {
      const price = new Price(next);

      if (!acc[price.dimensions.tripType]) {
        acc[price.dimensions.tripType] = {};
      }

      if (
        price.dimensions.geographicalArea &&
        !acc[price.dimensions.tripType]?.[price.dimensions.geographicalArea]
      ) {
        acc[price.dimensions.tripType][price.dimensions.geographicalArea] = {};
      }

      if (
        price.dimensions.tripDuration &&
        !acc[price.dimensions.tripType]?.[price.dimensions.geographicalArea]?.[
          price.dimensions.tripDuration
        ]
      ) {
        acc[price.dimensions.tripType][price.dimensions.geographicalArea][
          price.dimensions.tripDuration
        ] = new Coverages(price.coverages);
      }

      if (
        price.dimensions.familySituation &&
        !acc[price.dimensions.tripType]?.[price.dimensions.variant]
      ) {
        acc[price.dimensions.tripType][price.dimensions.variant] = {};
      }

      if (
        price.dimensions.variant &&
        !acc[price.dimensions.tripType]?.[price.dimensions.variant]?.[
          price.dimensions.familySituation
        ]
      ) {
        acc[price.dimensions.tripType][price.dimensions.variant][
          price.dimensions.familySituation
        ] = new Coverages(price.coverages);
      }
      if (
        price.dimensions.tripType &&
        price.dimensions.familySituation &&
        !price.dimensions.variant
      ) {
        acc[price.dimensions.tripType][
          price.dimensions.familySituation
        ] = new Coverages(price.coverages);
      }

      return acc;
    }, {} as { [key: string]: any });

    return data;
  }

  getPaymentRythmKey(paymentRythm: PaymentRythm) {
    return paymentRythm === PaymentRythm.YEARLY
      ? 'yearlyPremium'
      : 'monthlyPremium';
  }

  getPriceSingleTripEurope(
    tripDuration?: TripDuration,
    paymentRythm: PaymentRythm = PaymentRythm.YEARLY,
  ): number {
    if (!tripDuration) {
      return 0;
    }
    return (
      this.priceInfo?.[TripType.TRIP_TYPE_SINGLE_TRIP]?.[
        GeographicalArea.GEOGRAPHICAL_AREA_EUROPE
      ]?.[tripDuration]?.TRAVEL?.[this.getPaymentRythmKey(paymentRythm)]
        ?.withTaxes ?? 0
    );
  }

  getPriceSingleTripWorld(
    tripDuration?: TripDuration,
    paymentRythm: PaymentRythm = PaymentRythm.YEARLY,
  ) {
    if (!tripDuration) {
      return 0;
    }
    return (
      this.priceInfo?.[TripType.TRIP_TYPE_SINGLE_TRIP]?.[
        GeographicalArea.GEOGRAPHICAL_AREA_WORLD
      ]?.[tripDuration]?.TRAVEL?.[this.getPaymentRythmKey(paymentRythm)]
        ?.withTaxes ?? 0
    );
  }

  getPriceYearTrip(
    familySituation?: FamilySituation,
    paymentRythm: PaymentRythm = PaymentRythm.MONTHLY,
  ): number {
    if (!familySituation) {
      return 0;
    }
    return (
      this.priceInfo?.[TripType.TRIP_TYPE_YEAR_TRIP]?.[familySituation]
        ?.TRAVEL?.[this.getPaymentRythmKey(paymentRythm)]?.withTaxes ?? 0
    );
  }

  getMultiTripStandard(
    familySituation?: FamilySituation,
    paymentRythm: PaymentRythm = PaymentRythm.MONTHLY,
  ): number {
    if (!familySituation) {
      return 0;
    }

    return (
      this.priceInfo?.[TripType.TRIP_TYPE_MULTI_TRIP]?.[
        Variant.VARIANT_STANDARD
      ]?.[familySituation]?.TRAVEL?.[this.getPaymentRythmKey(paymentRythm)]
        ?.withTaxes ?? 0
    );
  }

  getMultiTripPremium(
    familySituation?: FamilySituation,
    paymentRythm: PaymentRythm = PaymentRythm.MONTHLY,
  ): number {
    if (!familySituation) {
      return 0;
    }
    return (
      this.priceInfo?.[TripType.TRIP_TYPE_MULTI_TRIP]?.[
        Variant.VARIANT_PREMIUM
      ]?.[familySituation]?.TRAVEL?.[this.getPaymentRythmKey(paymentRythm)]
        ?.withTaxes ?? 0
    );
  }

  getOptionsFromDraft(
    draft?: Draft,
    coverageType: 'to-be-paid' | 'base' = 'to-be-paid',
  ) {
    const terms = draft?.terms;
    const isSingleTrip = terms?.tripType === TripType.TRIP_TYPE_SINGLE_TRIP;
    const isMultiTrip = terms?.tripType === TripType.TRIP_TYPE_MULTI_TRIP;
    const isYearTrip = draft?.terms?.tripType === TripType.TRIP_TYPE_YEAR_TRIP;
    const priceInfo =
      coverageType === 'to-be-paid' ? this.priceInfo : this.basePriceInfo;
    if (isSingleTrip && terms?.geographicalArea && terms?.tripDuration) {
      return priceInfo?.[TripType.TRIP_TYPE_SINGLE_TRIP]?.[
        terms?.geographicalArea
      ]?.[terms?.tripDuration];
    } else if (isMultiTrip && terms?.variant && terms?.familySituation) {
      return priceInfo?.[TripType.TRIP_TYPE_MULTI_TRIP]?.[terms?.variant]?.[
        terms?.familySituation
      ];
    } else if (isYearTrip && terms?.familySituation) {
      return priceInfo?.[TripType.TRIP_TYPE_YEAR_TRIP]?.[
        terms?.familySituation
      ];
    }
  }

  getDifferenceWithDiscount(draft?: Draft, paymentRythm?: PaymentRythm) {
    const currentPrice = draft?.getTotalPrice(paymentRythm) ?? 0;
    const basePrice =
      this.getOptionsFromDraft(draft, 'base')?.computeTotalCoverage(
        draft?.terms,
      ) ?? 0;

    return currentPrice - basePrice;
  }
}

export default PriceInfo;
